// 브라우저 구현.
//
// ## 토큰을 `localStorage` 에 두지 않는다
// XSS 하나면 전부 털린다. 접근 토큰은 **메모리에만** 두고, 갱신은 `HttpOnly`
// 쿠키에 담긴 리프레시로 한다. 새로고침하면 다시 갱신 요청을 하면 된다 —
// 그 한 번의 왕복이 유출 위험보다 훨씬 싸다.

import { is_outward, type NotifyPayload, type Platform, type PushSource } from './index.ts'

/** 알림을 누르면 여기로 좌표가 온다. 창은 하나라 듣는 쪽도 하나면 된다. */
const listeners = new Set<(hash: string) => void>()

// 창이 꺼져 있을 때 온 알림은 서비스 워커가 띄운다. 누르면 워커가 이 창에 좌표를 보낸다.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
    const data = e.data as { type?: unknown; hash?: unknown } | null
    if (data?.type !== 'maru:link' || typeof data.hash !== 'string') return
    for (const listen of listeners) listen(data.hash)
  })
}

const b64url = (s: string) => s.replace(/=+$/, '')

function same_key(a: ArrayBuffer | null, key: string): boolean {
  if (a === null) return false
  const bytes = Uint8Array.from(atob(key.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
    c.charCodeAt(0),
  )
  const have = new Uint8Array(a)
  return have.length === bytes.length && have.every((b, i) => b === bytes[i])
}

/** 서버 `web_ok` 가 받는 **한 모양**: 칸 순서 고정, 채움 없는 base64url. */
export function subscription_token(sub: PushSubscriptionJSON): string | null {
  const { endpoint, keys } = sub
  if (!endpoint || !keys?.p256dh || !keys.auth) return null
  return JSON.stringify({ endpoint, p256dh: b64url(keys.p256dh), auth: b64url(keys.auth) })
}

/**
 * 웹 푸시 구독. 권한을 **묻지 않는다** — 알림 권한은 `notify` 가 처음 알림 때 묻고,
 * 로그인하자마자 뜨는 권한 창은 브라우저가 벌점을 준다. 이미 허락했을 때만 구독한다.
 */
function web_push(): PushSource | null {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  if (!('PushManager' in globalThis) || !('Notification' in globalThis)) return null
  return {
    needs_key: true,
    token: async (key) => {
      if (key === undefined || Notification.permission !== 'granted') return null
      // 개발 서버는 워커를 걸지 않는다 — `ready` 를 기다리면 영영 안 끝난다.
      const reg = await navigator.serviceWorker.getRegistration()
      if (reg === undefined) return null
      let sub = await reg.pushManager.getSubscription()
      // 서버 키가 바뀌었으면 옛 구독으로는 못 받는다. 풀고 새로.
      if (sub !== null && !same_key(sub.options.applicationServerKey, key)) {
        await sub.unsubscribe()
        sub = null
      }
      sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
      const token = subscription_token(sub.toJSON())
      return token === null ? null : { kind: 'web', token }
    },
    // 브라우저가 구독을 갈면 다음 로드 때 `token` 이 새 것을 건다(옛 것은 서버가 410 에 뗀다).
    changed: () => () => {},
  }
}

export function WebPlatform(): Platform {
  // 탭 제목은 안 읽음 수와 지금 자리 둘로 짓는다 — `(3) #디자인 - 마루 팀 - 마루`.
  let unread = 0
  let here: string | null = null
  const retitle = () => {
    document.title = `${unread > 0 ? `(${unread}) ` : ''}${here ? `${here} - ` : ''}마루`
  }
  return {
    name: '웹',

    keep: {
      // IndexedDB 가 아니라 localStorage 인 이유: 여기 담는 것은
      // **잃어도 되는 것**(마지막 본 채널, 사이드바 접힘)뿐이다.
      get: async (k) => localStorage.getItem(`maru:${k}`),
      put: async (k, v) => localStorage.setItem(`maru:${k}`, v),
      remove: async (k) => localStorage.removeItem(`maru:${k}`),
    },

    vault: null,

    notify: async (a: NotifyPayload) => {
      if (!('Notification' in globalThis)) return
      if (Notification.permission === 'default') await Notification.requestPermission()
      if (Notification.permission !== 'granted') return
      // `exactOptionalPropertyTypes` 가 켜져 있어 `undefined` 를 그냥 넘길 수 없다.
      // 이 엄격함이 "옵션을 안 준 것" 과 "undefined 를 준 것" 을 갈라 준다.
      const n = new Notification(
        a.subject,
        a.deep_link ? { body: a.body, tag: a.deep_link } : { body: a.body },
      )
      const hash = a.deep_link
      if (hash !== undefined) {
        n.onclick = () => {
          globalThis.focus()
          for (const listen of listeners) listen(hash)
          n.close()
        }
      }
    },

    alerts: async () => ('Notification' in globalThis ? Notification.permission : 'unsupported'),

    badge: (count) => {
      // 탭 제목이 가장 확실하다. Badging API 는 설치형 PWA 에서만 동작한다.
      unread = count
      retitle()
      const badging = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void> }
      void badging.setAppBadge?.(count)
    },

    place: (label) => {
      here = label
      retitle()
    },

    visible: () => document.visibilityState === 'visible',

    open_file: async (route) => void globalThis.open(route, '_blank', 'noopener,noreferrer'),

    open_url: async (url) => {
      if (is_outward(url)) globalThis.open(url, '_blank', 'noopener,noreferrer')
    },

    links: (listen) => {
      listeners.add(listen)
      return () => void listeners.delete(listen)
    },

    push: web_push(),
  }
}
